import { useState, useEffect } from 'react';
import RegisterRoomsCard from '@components/RegisterRoomsCard';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSessionStore } from '@stores/sessionStore';
import { promptVerifyToOrder } from '@utils/verifyToOrder';
import { useTranslation } from '@stores/translationStore';
import { useDomainStore } from '@stores/domainStore';
import { useSignalR } from '../hooks/useSignalR';
import { resolveDisplayValue } from '../i18n';
import { apiClient } from '@services/api';

import LanguageSwitcher from '@components/LanguageSwitcher';
import MiniChat from '@components/MiniChat';
import ConnectedDevicesModal from '@components/ConnectedDevicesModal';
import ActiveUpdates from '@components/ActiveUpdates';
import { QRCodeSVG } from 'qrcode.react';

// Icons and descriptions for service modules
const moduleConfig: Record<string, { emoji: string; descKey: string; defaultDesc: string }> = {
  housekeeping: { emoji: '🛎️', descKey: 'welcome.housekeepingDesc', defaultDesc: 'Room cleaning & fresh towels' },
  maintenance: { emoji: '🔧', descKey: 'welcome.maintenanceDesc', defaultDesc: 'Report issues & request repairs' },
  roomservice: { emoji: '🍽️', descKey: 'welcome.roomserviceDesc', defaultDesc: 'Order food & drinks to your room' },
  spa: { emoji: '💆', descKey: 'welcome.spaDesc', defaultDesc: 'Book spa treatments & wellness' },
  concierge: { emoji: '🎩', descKey: 'welcome.conciergeDesc', defaultDesc: 'Tours, reservations & recommendations' },
  minibar: { emoji: '🍷', descKey: 'welcome.minibarDesc', defaultDesc: 'Order drinks & snacks' },
  guides: { emoji: '📖', descKey: 'welcome.guidesDesc', defaultDesc: 'Step-by-step guides & information' },
  vouchers: { emoji: '🎟️', descKey: 'welcome.vouchersDesc', defaultDesc: 'Your vouchers & benefits' },
  laundry: { emoji: '👔', descKey: 'welcome.laundryDesc', defaultDesc: 'Laundry & dry cleaning service' },
  folio: { emoji: '🧾', descKey: 'welcome.folioDesc', defaultDesc: 'View your bill & charges' },
  registration: { emoji: '📝', descKey: 'welcome.registrationDesc', defaultDesc: 'Your details & signature before arrival' },
  documents: { emoji: '🪪', descKey: 'welcome.documentsDesc', defaultDesc: 'Upload your passport or ID' },
  review: { emoji: '⭐', descKey: 'welcome.reviewDesc', defaultDesc: 'Tell us how your stay was' },
  lost_property: { emoji: '🧳', descKey: 'welcome.lostPropertyDesc', defaultDesc: 'Left something behind?' },
};

// Get hotel slug from domainStore, URL params, or localStorage fallback
const getHotelSlug = (paramSlug: string | undefined, domainSlug: string | null | undefined): string | null => {
  if (domainSlug) return domainSlug;
  if (paramSlug && paramSlug !== 'undefined') return paramSlug;
  return localStorage.getItem('gcxp_hotel_slug');
};

interface SessionDetails {
  sessionId: string;
  roomNumber?: string;
  guestName?: string;
  guestCount: number;
  checkInAt?: string;
  checkOutAt?: string;
  devicesActivated: number;
  maxDevices: number;
  availableModules: string[];
  hotelName: string;
  shareUrl: string;
  availableStations?: { id: number; name: string; iconEmoji?: string; color?: string }[];
  sessionType?: string;  // e.g., "hotel_room", "restaurant", "spa"
  displayLabel?: string; // Human-readable label for non-room sessions
  // Session type settings
  requiresIdentifier?: boolean;
  identifierLabel?: string;
  requiresPhoneVerification?: boolean;
  allowMultipleDevices?: boolean;
  // The stage of the stay, and — before arrival — the booked arrival and registration progress.
  stage?: 'PreCheckIn' | 'InHouse' | 'PostCheckout' | null;
  arrivalDate?: string;
  registrationComplete?: boolean | null;
}

interface LocationState {
  justActivated?: boolean;
  token?: string;
  sessionId?: string;
  hotelSlug?: string;
  hotelName?: string;
  guestName?: string;
  roomNumber?: string;
  guestCount?: number;
  deviceNumber?: number;
  maxDevices?: number;
  availableModules?: string[];
  availableStations?: { id: number; name: string; iconEmoji?: string; color?: string }[];
  checkInAt?: string;
  checkOutAt?: string;
  sessionType?: string;
  displayLabel?: string;
  scope?: string;
  scopeValue?: string;
}

/** The last details this page showed, per hotel and session (token), for an instant re-show. */
const welcomeCache = new Map<string, { details: SessionDetails; usage: Record<string, number> }>();
const welcomeKey = (slug: string) => {
  let token: string | null = null;
  try { token = localStorage.getItem('gcxp_token'); } catch { /* no storage */ }
  return `${slug}|${token ?? ''}`;
};

export default function WelcomePage() {
  const { hotelSlug: paramSlug } = useParams<{ hotelSlug: string }>();
  const { config } = useDomainStore();
  const hotelSlug = getHotelSlug(paramSlug, config?.hotelSlug);
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const { isValid, refreshSession } = useSessionStore();
  const { personalName } = useSessionStore();
  const { session, isVerifierDevice } = useSessionStore();
  const { t } = useTranslation();
  const { on } = useSignalR();
  
  // Coming back to this page shows what it showed last time at once (no skeleton, no layout jump — iOS
  // swipe-back has just shown the finished page) while fresh details always load behind it, so removed
  // modules/stations still go within a moment. First visit: skeleton until the API answers.
  const cached = hotelSlug ? welcomeCache.get(welcomeKey(hotelSlug)) : undefined;
  const [details, setDetails] = useState<SessionDetails | null>(cached?.details ?? null);
  const [usageCounts, setUsageCounts] = useState<Record<string, number>>(cached?.usage ?? {});
  const [isLoading, setIsLoading] = useState(!cached);
  const [isRequestingDevice, setIsRequestingDevice] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for hotelSlug to be available
    if (!hotelSlug) {
      return;
    }

    const controller = new AbortController();

    // If we just activated, show data from router state immediately (no loading flash)
    // Then always fetch fresh data from API to get accurate device counts etc.
    if (locationState?.justActivated) {
      setDetails({
        sessionId: locationState.sessionId || '',
        roomNumber: locationState.roomNumber,
        guestName: locationState.guestName,
        guestCount: locationState.guestCount ?? 1,
        devicesActivated: locationState.deviceNumber ?? 1,
        maxDevices: locationState.maxDevices ?? 1,
        availableModules: locationState.availableModules || [],
        availableStations: locationState.availableStations,
        checkInAt: locationState.checkInAt,
        checkOutAt: locationState.checkOutAt,
        hotelName: locationState.hotelName || '',
        shareUrl: '',
      });
      setIsLoading(false);
    }

    // Always load fresh session details from API
    loadSessionDetails(controller.signal);
    return () => controller.abort();
  }, [hotelSlug]);

  // Listen for real-time session updates (when admin changes services)
  useEffect(() => {
    const unsub = on('SessionUpdated', () => {
      loadSessionDetails(); // Reload to get updated modules/stations
    });
    return unsub;
  }, [on, hotelSlug]);

  const loadSessionDetails = async (signal?: AbortSignal) => {
    if (!hotelSlug) return;

    try {
      // Load details and usage counts in parallel to avoid layout shift
      const [detailsRes, usageRes] = await Promise.all([
        apiClient.get(`/api/v1/${hotelSlug}/session/details`, { signal }),
        apiClient.get(`/api/v1/${hotelSlug}/session/usage`, { signal }).catch(() => null)
      ]);

      if (detailsRes.data.success) {
        // Set usage counts first (if available) so sorting is ready
        if (usageRes?.data?.success && usageRes.data.data) {
          setUsageCounts(usageRes.data.data);
        }
        const usage = usageRes?.data?.success && usageRes.data.data ? usageRes.data.data : undefined;
        setDetails(prev => {
          const next = {
            ...detailsRes.data.data,
            shareUrl: detailsRes.data.data.shareUrl || prev?.shareUrl || '',
          };
          welcomeCache.set(welcomeKey(hotelSlug), { details: next, usage: usage ?? welcomeCache.get(welcomeKey(hotelSlug))?.usage ?? {} });
          return next;
        });
        // Sync sessionStore with fresh data from details endpoint
        // This ensures ModuleGuard has the correct availableModules
        await refreshSession();
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      console.error('Failed to load session details:', err);
      setError('Failed to load session details');
    } finally {
      setIsLoading(false);
    }
  };

  const trackUsage = async (moduleSlug?: string, stationId?: number) => {
    if (!hotelSlug) return;
    try {
      await apiClient.post(`/api/v1/${hotelSlug}/session/usage`, {
        moduleSlug,
        kitchenStationId: stationId,
        action: 'view'
      });
      // Update local counts
      const key = stationId ? `station:${stationId}` : moduleSlug;
      if (key) {
        setUsageCounts(prev => ({
          ...prev,
          [key]: (prev[key] || 0) + 1
        }));
      }
    } catch (err) {
      console.error('Failed to track usage:', err);
    }
  };

  const handleServiceClick = (moduleSlug: string) => {
    trackUsage(moduleSlug);
    navigate(hotelSlug ? `/${hotelSlug}/${moduleSlug}` : `/${moduleSlug}`);
  };

  const handleStationClick = (stationId: number) => {
    trackUsage(undefined, stationId);
    navigate(hotelSlug ? `/${hotelSlug}/station/${stationId}` : `/station/${stationId}`);
  };

  const requestAdditionalDevice = async () => {
    setIsRequestingDevice(true);
    setError(null);
    
    try {
      const response = await apiClient.post<{ success: boolean; data: { maxDevices: number; shareUrl: string } }>(`/api/v1/${hotelSlug}/session/request-device`);
      if (response.data.success) {
        setDetails(prev => prev ? {
          ...prev,
          maxDevices: response.data.data.maxDevices,
          shareUrl: response.data.data.shareUrl,
        } : null);
        setShowShareModal(true);
      }
    } catch (err: unknown) {
      console.error('Failed to request additional device:', err);
      const e = err as { response?: { data?: { Error?: string } } };
      setError(e.response?.data?.Error || 'Failed to request additional device slot');
    } finally {
      setIsRequestingDevice(false);
    }
  };

  const copyShareLink = async () => {
    if (details?.shareUrl) {
      try {
        await navigator.clipboard.writeText(details.shareUrl);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = details.shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      }
    }
  };

  const shareLink = async () => {
    if (details?.shareUrl && navigator.share) {
      try {
        await navigator.share({
          title: `${details.hotelName} - Guest Access`,
          text: 'Scan to access hotel services',
          url: details.shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed, fall back to copy
        copyShareLink();
      }
    } else {
      copyShareLink();
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        weekday: 'short', 
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="page-header">
          <div className="placeholder-glow">
            <span className="placeholder col-6" style={{ height: '1.4rem', borderRadius: '0.5rem' }}></span>
            <span className="placeholder col-3 mt-2 d-block" style={{ height: '0.9rem', borderRadius: '0.5rem', opacity: 0.5 }}></span>
          </div>
        </div>
        <div className="py-3 placeholder-glow">
          {/* Room / Guests card */}
          <div className="card mb-4 shadow-sm">
            <div className="card-body">
              <div className="row">
                <div className="col-6 text-center border-end">
                  <span className="placeholder col-5 d-block mx-auto mb-1" style={{ height: '0.7rem' }}></span>
                  <span className="placeholder col-3 d-block mx-auto" style={{ height: '2rem' }}></span>
                </div>
                <div className="col-6 text-center">
                  <span className="placeholder col-5 d-block mx-auto mb-1" style={{ height: '0.7rem' }}></span>
                  <span className="placeholder col-3 d-block mx-auto" style={{ height: '2rem' }}></span>
                </div>
              </div>
            </div>
          </div>
          {/* Stay dates card */}
          <div className="card mb-4 shadow-sm">
            <div className="card-body">
              <span className="placeholder col-3 mb-3 d-block" style={{ height: '0.8rem' }}></span>
              <div className="row">
                <div className="col-6"><span className="placeholder col-8" style={{ height: '0.9rem' }}></span></div>
                <div className="col-6"><span className="placeholder col-8" style={{ height: '0.9rem' }}></span></div>
              </div>
            </div>
          </div>
          {/* Service rows */}
          {[1, 2, 3].map(i => (
            <div key={i} className="card mb-3 shadow-sm">
              <div className="card-body d-flex align-items-center">
                <span className="placeholder rounded-circle me-3" style={{ width: '2.5rem', height: '2.5rem', flexShrink: 0 }}></span>
                <div className="flex-grow-1">
                  <span className="placeholder col-4 d-block mb-1" style={{ height: '0.9rem' }}></span>
                  <span className="placeholder col-6" style={{ height: '0.7rem', opacity: 0.5 }}></span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Check if we have a valid session - either from store, router state, or loaded details
  const hasValidSession = isValid || locationState?.justActivated || details !== null;
  
  if (!hasValidSession || (!details && !locationState?.justActivated)) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 p-4" style={{ color: 'var(--theme-text-color, #212529)' }}>
        <div className="text-center animate-fade-in">
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.6 }}>🔒</div>
          <h2 className="h4 mb-3">{t('welcome.sessionExpired')}</h2>
          <p style={{ opacity: 0.6, maxWidth: 280, margin: '0 auto' }}>{t('welcome.scanAgain')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Welcome Header */}
      <div className="page-header">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h2>{t('welcome.welcome')}{personalName ? `, ${personalName}` : (details?.guestName ? `, ${details.guestName}` : '')}!</h2>
            {personalName && details?.guestName && personalName !== details.guestName && (
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', marginTop: '-4px' }}>{details.guestName}</p>
            )}
            <p>{resolveDisplayValue(details?.hotelName, t)}</p>
          </div>
          <div className="d-flex flex-column align-items-end gap-2">
            <div className="welcome-language-switcher">
              <LanguageSwitcher variant="globe" />
            </div>
            {/* Device status as small badges: green phone = this device is verified (may order/charge),
                star = it is the primary device (the one that verified). */}
            {session?.deviceTrusted === true && (
              <div className="welcome-device-badges">
                <span className="welcome-device-badge verified" title="Phone verified" aria-label="Phone verified" role="img">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="2" width="12" height="20" rx="2.5" />
                    <polyline points="9 12 11 14 15 10" />
                  </svg>
                </span>
                {isVerifierDevice() && (
                  <span className="welcome-device-badge primary" title="Primary device" aria-label="Primary device" role="img">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6L2.5 9.4l6.6-.8z" />
                    </svg>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="py-3">
        {/* Verification Card — shown to any device that can't charge/use folio yet (browse-only), whether
            the session is unverified OR this is a browse-only device on an already-verified session. */}
        {!session?.deviceTrusted && (details?.requiresPhoneVerification || session?.requiresPhoneVerification || (session?.trustLevel ?? 0) >= 2) && (
          <div className="card mb-4 shadow-sm border-0" style={{ 
            background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)',
            borderLeft: '4px solid var(--theme-color-1, #0d6efd) !important'
          }}>
            <div className="card-body d-flex align-items-center">
              <div className="flex-shrink-0 me-3" style={{ fontSize: '2rem' }}>🔒</div>
              <div className="flex-grow-1">
                <h6 className="card-title mb-1" style={{ fontWeight: 700 }}>
                  {t('welcome.verifyRoom') !== 'welcome.verifyRoom' ? t('welcome.verifyRoom') : 'Verify Your Room'}
                </h6>
                <p className="text-muted small mb-0">
                  {t('welcome.verifyDesc') !== 'welcome.verifyDesc' ? t('welcome.verifyDesc') : 'Verify your phone to unlock folio charges and allow additional devices to session.'}
                </p>
              </div>
              <button 
                className="btn btn-sm ms-2 flex-shrink-0"
                style={{ 
                  background: 'var(--theme-color-1, #0d6efd)', 
                  color: '#fff',
                  borderRadius: '0.75rem',
                  fontWeight: 600,
                  padding: '0.5rem 1rem'
                }}
                onClick={() => promptVerifyToOrder()}
              >
                {t('welcome.verifyNow') !== 'welcome.verifyNow' ? t('welcome.verifyNow') : 'Verify'}
              </button>
            </div>
          </div>
        )}

        {/* Mini Chat — always show for active sessions */}
        <MiniChat hotelSlug={hotelSlug} />

        {/* Live voice call with the AI concierge (the call screen asks for the call on its own tap) */}
        {details?.availableModules?.includes('voice_call') && (
          <button
            className="card mb-4 shadow-sm service-card-item w-100 text-start border-0"
            style={{ cursor: 'pointer', borderLeft: '4px solid var(--theme-color-1, #0d6efd)' }}
            onClick={() => {
              trackUsage('voice_call');
              // The call runs over the chat, where its cards, updates and photos show up; the call
              // screen stays for a stay without messages.
              const page = details?.availableModules?.includes('messages') ? 'messages?call=1' : 'voice-call';
              navigate(hotelSlug ? `/${hotelSlug}/${page}` : `/${page}`);
            }}
          >
            <div className="card-body d-flex align-items-center">
              <span className="service-card-emoji">📞</span>
              <div className="flex-grow-1">
                <h6 className="card-title mb-1" style={{ fontWeight: 600 }}>
                  {t('welcome.voiceCall') !== 'welcome.voiceCall' ? t('welcome.voiceCall') : 'Call the concierge'}
                </h6>
                <p className="text-muted small mb-0">
                  {t('welcome.voiceCallDesc') !== 'welcome.voiceCallDesc' ? t('welcome.voiceCallDesc') : 'Talk to our assistant — ask anything or make a request'}
                </p>
              </div>
              <svg className="service-card-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          </button>
        )}

        {/* Active Orders & Requests carousel */}
        <ActiveUpdates hotelSlug={hotelSlug} />

        {/* The main booker's other rooms (they share the booker's contact): share access, or give each its own */}
        <RegisterRoomsCard onOpen={() => navigate(hotelSlug ? `/${hotelSlug}/my-rooms` : '/my-rooms')} />

        {/* Before arrival: when the stay starts, and what is left to do before it does */}
        {details?.stage === 'PreCheckIn' && (
          <div className="card mb-4 shadow-sm">
            <div className="card-body">
              <h6 className="card-title text-muted mb-2">{t('welcome.beforeYouArrive') !== 'welcome.beforeYouArrive' ? t('welcome.beforeYouArrive') : 'Before you arrive'}</h6>
              {details.arrivalDate && (
                <p className="mb-2">
                  {t('welcome.arrivingOn') !== 'welcome.arrivingOn' ? t('welcome.arrivingOn') : 'Arriving'}{' '}
                  <strong>{formatDate(details.arrivalDate)}</strong>
                </p>
              )}
              {details.registrationComplete ? (
                <p className="text-success small mb-0">
                  ✓ {t('welcome.registrationDone') !== 'welcome.registrationDone' ? t('welcome.registrationDone') : 'Your registration is complete. See you soon!'}
                </p>
              ) : details.availableModules?.includes('registration') && (
                <button className="btn btn-primary w-100" onClick={() => handleServiceClick('registration')}>
                  {t('welcome.completeRegistration') !== 'welcome.completeRegistration' ? t('welcome.completeRegistration') : 'Complete your registration'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* After checkout: thanks, and the way to rate the stay */}
        {details?.stage === 'PostCheckout' && (
          <div className="card mb-4 shadow-sm">
            <div className="card-body">
              <h6 className="card-title text-muted mb-2">{t('welcome.thanksForStaying') !== 'welcome.thanksForStaying' ? t('welcome.thanksForStaying') : 'Thank you for staying with us'}</h6>
              <p className="small text-muted mb-3">
                {t('welcome.postStayHint') !== 'welcome.postStayHint' ? t('welcome.postStayHint') : 'Your invoice, messages and lost property are here for a few more days.'}
              </p>
              {details.availableModules?.includes('review') && (
                <button className="btn btn-primary w-100" onClick={() => handleServiceClick('review')}>
                  {t('welcome.rateStay') !== 'welcome.rateStay' ? t('welcome.rateStay') : 'Rate your stay'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Room Info Card */}
        {details?.stage !== 'PreCheckIn' && details?.stage !== 'PostCheckout' && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <div className="row">
              {/* Show room number for hotel_room sessions, or displayLabel for non-room sessions */}
              {(details?.roomNumber || details?.displayLabel) && (
                <div className="col-6 text-center border-end">
                  <div className="text-muted small">
                    {details?.sessionType && details.sessionType !== 'hotel_room' 
                      ? (details.sessionType === 'restaurant' ? t('welcome.table') || 'Table'
                        : details.sessionType === 'spa' ? t('welcome.spaSession') || 'Session'
                        : details.sessionType === 'event' ? t('welcome.event') || 'Event'
                        : details.sessionType === 'daypass' ? t('welcome.dayPass') || 'Day Pass'
                        : details.sessionType === 'poolbar' ? t('welcome.poolbar') || 'Pool Bar'
                        : details.sessionType === 'lounge' ? t('welcome.lounge') || 'Lounge'
                        : t('welcome.sessionLabel') || 'Session')
                      : t('welcome.roomNumber')}
                  </div>
                  <div className="h2 mb-0 text-primary">{details?.roomNumber || details?.displayLabel}</div>
                </div>
              )}
              <div className={(details?.roomNumber || details?.displayLabel) ? "col-6 text-center" : "col-12 text-center"}>
                <div className="text-muted small">{t('welcome.guests')}</div>
                <div className="h2 mb-0 text-primary">{details?.guestCount || 1}</div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Stay Dates Card (from arrival on — before it, the card above shows the booking) */}
        {details?.stage !== 'PreCheckIn' && (details?.checkInAt || details?.checkOutAt) && (
          <div className="card mb-4 shadow-sm">
            <div className="card-body">
            <h6 className="card-title text-muted mb-3">{t('welcome.yourStay')}</h6>
            <div className="row">
              <div className="col-6">
                <div className="small text-muted">{t('welcome.checkIn')}</div>
                  <div className="fw-semibold">{formatDate(details?.checkInAt)}</div>
                </div>
                <div className="col-6">
                <div className="small text-muted">{t('welcome.checkOut')}</div>
                  <div className="fw-semibold">{formatDate(details?.checkOutAt)}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Kitchen Station Service Cards */}
        {details?.availableStations && details.availableStations.length > 0 && (
          <>
            <h6 className="text-muted mb-3 mt-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              {t('welcome.ordering') !== 'welcome.ordering' ? t('welcome.ordering') : 'Ordering'}
            </h6>
            {[...details.availableStations]
              .sort((a, b) => (usageCounts[`station:${b.id}`] || 0) - (usageCounts[`station:${a.id}`] || 0))
              .map(station => (
          <button
            key={station.id}
            className="card mb-3 shadow-sm service-card-item w-100 text-start border-0"
            style={{ cursor: 'pointer', borderLeft: `4px solid ${station.color || 'var(--theme-color-1, #0d6efd)'}` }}
            onClick={() => handleStationClick(station.id)}
          >
            <div className="card-body d-flex align-items-center">
              <span className="service-card-emoji">{station.iconEmoji || '🍽️'}</span>
              <div className="flex-grow-1">
                <h6 className="card-title mb-1" style={{ fontWeight: 600 }}>{station.name}</h6>
                <p className="text-muted small mb-0">{t('welcome.orderNow') !== 'welcome.orderNow' ? t('welcome.orderNow') : 'Tap to view menu & order'}</p>
              </div>
              <svg className="service-card-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          </button>
        ))}
          </>
        )}

        {/* Service Module Cards */}
        {details?.availableModules && details.availableModules.length > 0 && (
          <>
            {details.availableModules.length > 0 && (details.availableStations?.length ?? 0) > 0 && (
              <h6 className="text-muted mb-3 mt-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {t('welcome.services') !== 'welcome.services' ? t('welcome.services') : 'Services'}
              </h6>
            )}
            {[...details.availableModules]
              .sort((a, b) => (usageCounts[b] || 0) - (usageCounts[a] || 0))
              .map(moduleSlug => {
              const config = moduleConfig[moduleSlug];
              if (!config) return null;
              const desc = t(config.descKey) !== config.descKey ? t(config.descKey) : config.defaultDesc;
              const moduleName = t(`module.${moduleSlug}`) !== `module.${moduleSlug}`
                ? t(`module.${moduleSlug}`)
                : moduleSlug.charAt(0).toUpperCase() + moduleSlug.slice(1);
              return (
                <button
                  key={moduleSlug}
                  className="card mb-3 shadow-sm service-card-item w-100 text-start border-0"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleServiceClick(moduleSlug)}
                >
                  <div className="card-body d-flex align-items-center">
                    <span className="service-card-emoji">{config.emoji}</span>
                    <div className="flex-grow-1">
                      <h6 className="card-title mb-1" style={{ fontWeight: 600 }}>{moduleName}</h6>
                      <p className="text-muted small mb-0">{desc}</p>
                    </div>
                    <svg className="service-card-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </div>
                </button>
              );
            })}
          </>
        )}

        {/* Device Info & Share Card — only shown when session allows multiple devices */}
        {(details?.allowMultipleDevices ?? session?.allowMultipleDevices ?? true) && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="card-title text-muted mb-0">{t('welcome.connectedDevices')}</h6>
              <span className="badge bg-primary">
                {details?.devicesActivated || 1} / {details?.maxDevices || 1}
              </span>
            </div>
            
            <p className="small text-muted mb-3">
              {t('welcome.shareDeviceDesc')}
            </p>

            <button
              onClick={requestAdditionalDevice}
              disabled={isRequestingDevice}
              className="btn btn-outline-primary w-100"
            >
              {isRequestingDevice ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {t('welcome.processing')}
                </>
              ) : (
                <>
                  ➕ {t('welcome.addDevice')}
                </>
              )}
            </button>

            <button
              onClick={() => setShowDevicesModal(true)}
              className="btn btn-link w-100 mt-2 p-0 text-decoration-none small"
            >
              📱 Manage devices
            </button>

            {error && (
              <div className="alert alert-danger mt-3 mb-0 py-2 small">
                {error}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Connected Devices manager */}
      {showDevicesModal && hotelSlug && (
        <ConnectedDevicesModal hotelSlug={hotelSlug} onClose={() => setShowDevicesModal(false)} />
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('welcome.shareModalTitle')}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowShareModal(false)}
                />
              </div>
              <div className="modal-body text-center">
                <div className="mb-3">
                  <span className="h1">📱</span>
                </div>
                <p className="text-muted mb-3">
                  {t('welcome.shareModalDesc')}
                </p>
                
                {details?.shareUrl && (
                  <div className="bg-light p-4 rounded mb-3 d-flex justify-content-center">
                    <QRCodeSVG 
                      value={details.shareUrl} 
                      size={180}
                      level="M"
                      includeMargin={true}
                    />
                  </div>
                )}

                <div className="d-grid gap-2">
                  <button onClick={shareLink} className="btn btn-primary">
                    📤 {t('welcome.shareLink')}
                  </button>
                  <button onClick={copyShareLink} className="btn btn-outline-secondary">
                    {shareSuccess ? `✅ ${t('welcome.copied')}` : `📋 ${t('welcome.copyLink')}`}
                  </button>
                </div>
              </div>
              <div className="modal-footer justify-content-center border-0">
                <small className="text-muted">
                  {t('welcome.deviceSlots')}: {details?.devicesActivated} / {details?.maxDevices}
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legal Footer */}
      <footer style={{ 
        marginTop: 'auto', 
        padding: '1rem 0', 
        textAlign: 'center', 
        fontSize: '0.75rem', 
        color: '#888',
        borderTop: '1px solid #eee'
      }}>
        <a href="https://guest.goldencrown-hotels.com/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#666', textDecoration: 'none', margin: '0 0.5rem' }}>
          {t('footer.privacy') || 'Privacy Policy'}
        </a>
        <span style={{ color: '#ccc' }}>|</span>
        <a href="https://guest.goldencrown-hotels.com/legal/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#666', textDecoration: 'none', margin: '0 0.5rem' }}>
          {t('footer.terms') || 'Terms of Service'}
        </a>
        <span style={{ color: '#ccc' }}>|</span>
        <a href="https://guest.goldencrown-hotels.com/legal/data-deletion" target="_blank" rel="noopener noreferrer" style={{ color: '#666', textDecoration: 'none', margin: '0 0.5rem' }}>
          {t('footer.dataDeletion') || 'Data Deletion'}
        </a>
      </footer>
    </div>
  );
}
